import React, {
	createContext,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/providers/AuthProvider";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";
import { buildTourSteps, resolveTarget, TourStepDef } from "@/components/tour/tourSteps";
import { seedSampleDataIfEmpty } from "@/utils/tour/seedSampleData";

const waitForElement = async (
	selector: string,
	timeoutMs = 2500,
	stableMs = 400,
): Promise<boolean> => {
	const deadline = Date.now() + timeoutMs;
	while (Date.now() < deadline) {
		if (!document.querySelector(selector)) {
			await new Promise((r) => setTimeout(r, 80));
			continue;
		}
		// Element present — require it to stay present for `stableMs` to avoid
		// anchoring to a node that's about to be unmounted by a loading flicker
		// (e.g. JobDetail's double-fetch replacing the page with a skeleton).
		const stableDeadline = Date.now() + stableMs;
		let stable = true;
		while (Date.now() < stableDeadline) {
			await new Promise((r) => setTimeout(r, 60));
			if (!document.querySelector(selector)) {
				stable = false;
				break;
			}
		}
		if (stable) return true;
	}
	return false;
};

export interface TourContextValue {
	run: boolean;
	stepIndex: number;
	steps: TourStepDef[];
	start: () => void;
	skip: () => void;
	finish: () => void;
	next: () => void;
	prev: () => void;
	goTo: (i: number) => void;
	hasCompleted: boolean;
	resolveStepTarget: (step: TourStepDef) => string;
	firstJobId: string | null;
	firstMatchId: string | null;
}

export const TourContext = createContext<TourContextValue | undefined>(undefined);

const localStorageKey = (userId: string) => `digger:tour:v1:completed:${userId}`;

const resolveRoute = (
	template: string | undefined,
	ids: { firstJobId: string | null; firstMatchId: string | null },
): string | undefined => {
	if (!template) return undefined;
	return template
		.replace(":jobId", ids.firstJobId ?? "")
		.replace(":matchId", ids.firstMatchId ?? "");
};

export const TourProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const { user, userProfile, currentOrganization, refreshUserData } = useAuth();
	const navigate = useNavigate();
	const location = useLocation();
	const isMobile = useIsMobile();

	const [run, setRun] = useState(false);
	const [stepIndex, setStepIndex] = useState(0);
	const [hasCompleted, setHasCompleted] = useState(false);
	const [firstJobId, setFirstJobId] = useState<string | null>(null);
	const [firstMatchId, setFirstMatchId] = useState<string | null>(null);
	const autoStartAttempted = useRef(false);
	const seedingInFlight = useRef(false);

	const steps = useMemo(() => buildTourSteps(), []);

	const resolveStepTarget = useCallback(
		(step: TourStepDef) => resolveTarget(step, isMobile),
		[isMobile],
	);

	useEffect(() => {
		if (!user?.id) {
			setHasCompleted(false);
			return;
		}
		const profileFlag = (userProfile as { tour_completed?: boolean } | null)?.tour_completed;
		if (profileFlag === true) {
			setHasCompleted(true);
			return;
		}
		if (profileFlag === false) {
			setHasCompleted(false);
			return;
		}
		const stored = window.localStorage.getItem(localStorageKey(user.id));
		setHasCompleted(stored === "1");
	}, [user?.id, userProfile]);

	const ensureSampleData = useCallback(async (): Promise<{ jobId: string | null; matchId: string | null }> => {
		if (!user?.id || !currentOrganization?.id)
			return { jobId: firstJobId, matchId: firstMatchId };
		if (seedingInFlight.current)
			return { jobId: firstJobId, matchId: firstMatchId };

		seedingInFlight.current = true;
		try {
			const result = await seedSampleDataIfEmpty(user.id, currentOrganization.id);
			setFirstJobId(result.firstJobId);
			setFirstMatchId(result.firstMatchId);
			return { jobId: result.firstJobId, matchId: result.firstMatchId };
		} finally {
			seedingInFlight.current = false;
		}
	}, [user?.id, currentOrganization?.id, firstJobId, firstMatchId]);

	const markCompleted = useCallback(async () => {
		if (!user?.id) return;
		window.localStorage.setItem(localStorageKey(user.id), "1");
		setHasCompleted(true);
		try {
			const { error } = await supabase
				.from("profiles")
				.update({ tour_completed: true, tour_completed_at: new Date().toISOString() })
				.eq("id", user.id);
			if (!error)
				refreshUserData?.();
		} catch {
			// DB column may not exist yet; localStorage is sufficient.
		}
	}, [user?.id, refreshUserData]);

	const start = useCallback(async () => {
		setStepIndex(0);
		await ensureSampleData();
		setRun(true);
	}, [ensureSampleData]);

	const skip = useCallback(() => {
		setRun(false);
		markCompleted();
	}, [markCompleted]);

	const finish = useCallback(() => {
		setRun(false);
		markCompleted();
	}, [markCompleted]);

	const goTo = useCallback(
		async (i: number) => {
			if (i < 0 || i >= steps.length) return;
			const target = steps[i];
			const resolvedRoute = resolveRoute(target.route, { firstJobId, firstMatchId });
			const selector = resolveTarget(target, isMobile);

			const crossRoute = !!resolvedRoute && window.location.pathname !== resolvedRoute;
			if (crossRoute) {
				setRun(false);
				setStepIndex(i);
				navigate(resolvedRoute!);
			}

			let ok = true;
			if (selector !== "body")
				ok = await waitForElement(selector, crossRoute ? 6000 : 3000);

			if (!ok) {
				// Target never appeared (e.g. non-published existing job has no
				// Globe indicator). Skip forward rather than leaving the user
				// stuck on an empty tour.
				if (i < steps.length - 1) {
					await goTo(i + 1);
					return;
				}
				finish();
				return;
			}

			if (crossRoute)
				await new Promise((r) => requestAnimationFrame(() => r(null)));
			setStepIndex(i);
			setRun(true);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[steps, location.pathname, navigate, isMobile, firstJobId, firstMatchId],
	);

	const next = useCallback(() => {
		if (stepIndex >= steps.length - 1) {
			finish();
			return;
		}
		goTo(stepIndex + 1);
	}, [stepIndex, steps.length, goTo, finish]);

	const prev = useCallback(() => {
		if (stepIndex <= 0) return;
		goTo(stepIndex - 1);
	}, [stepIndex, goTo]);

	useEffect(() => {
		if (autoStartAttempted.current) return;
		if (!user?.id) return;
		if (!currentOrganization?.id) return;
		if (hasCompleted) return;
		if (location.pathname !== "/") return;

		autoStartAttempted.current = true;
		const timer = window.setTimeout(async () => {
			await ensureSampleData();
			setStepIndex(0);
			setRun(true);
		}, 600);
		return () => window.clearTimeout(timer);
	}, [user?.id, currentOrganization?.id, hasCompleted, location.pathname, ensureSampleData]);

	const value: TourContextValue = useMemo(() => ({
		run,
		stepIndex,
		steps,
		start,
		skip,
		finish,
		next,
		prev,
		goTo,
		hasCompleted,
		resolveStepTarget,
		firstJobId,
		firstMatchId,
	}), [run, stepIndex, steps, start, skip, finish, next, prev, goTo, hasCompleted, resolveStepTarget, firstJobId, firstMatchId]);

	return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
};
